import { Stack, Button, Group, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { EditInsuranceFormData } from "../types"
import { DatePickerInput } from "@mantine/dates"
import { InfinitePersonSingleSelect } from "@components/SelectFromQuery/InfinitePersonSingleSelect"
import { DefaultModal } from "@components/default-modal"
import { DeleteConfirmModal } from "../../../../art/productions/$id/$roleId/-components/JobStepTab/modals/delete-confirm-modal"

interface EditInsuranceModalProps {
  opened: boolean
  onClose: () => void
  editInsuranceFormData: EditInsuranceFormData
  setEditInsuranceFormData: (data: EditInsuranceFormData) => void
  updateInsuranceMutation: {
    isPending: boolean
    error: unknown
    reset?: () => void
  }
  deleteInsuranceMutation: {
    isPending: boolean
    error: unknown
    reset?: () => void
  }
  insuranceId?: number
  onUpdate: (data: EditInsuranceFormData) => void
  onDelete: (insuranceId: number) => void
}

export function EditInsuranceModal({
  opened,
  onClose,
  editInsuranceFormData,
  setEditInsuranceFormData,
  updateInsuranceMutation,
  deleteInsuranceMutation,
  insuranceId,
  onUpdate,
  onDelete,
}: EditInsuranceModalProps) {
  const { t } = useTranslation("app_server")
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)

  return (
    <DefaultModal
      opened={opened}
      onClose={() => {
        onClose()
        updateInsuranceMutation.reset?.()
        deleteInsuranceMutation.reset?.()
        setShowDeleteConfirm(false)
      }}
      title={t("Edit")}
    >
      {opened && (
        <>
          <Stack gap="md">
            <InfinitePersonSingleSelect
              label={t("Owner")}
              value={editInsuranceFormData.insuredPersonId?.toString() ?? ""}
              onSelectionChange={(value) =>
                setEditInsuranceFormData({
                  ...editInsuranceFormData,
                  insuredPersonId: value ? parseInt(value) : null,
                })
              }
              searchable
              required
            />

            <DatePickerInput
              label={t("ValidSince")}
              value={editInsuranceFormData.startDate ?? undefined}
              onChange={(value) =>
                setEditInsuranceFormData({
                  ...editInsuranceFormData,
                  startDate: value ? new Date(value) : null,
                })
              }
            />

            <DatePickerInput
              label={t("ValidTo")}
              value={editInsuranceFormData.endDate ?? undefined}
              onChange={(value) =>
                setEditInsuranceFormData({
                  ...editInsuranceFormData,
                  endDate: value ? new Date(value) : null,
                })
              }
            />

            {updateInsuranceMutation.error ? (
              <Text color="red" size="sm">
                {t("FailedToUpdateInsurance")}
              </Text>
            ) : null}

            {deleteInsuranceMutation.error ? (
              <Text color="red" size="sm">
                {t("FailedToDeleteInsurance")}
              </Text>
            ) : null}

            <Group justify="space-between" gap="md">
              <Button
                variant="subtle"
                color="red"
                onClick={() => setShowDeleteConfirm(true)}
                loading={deleteInsuranceMutation.isPending}
              >
                {t("Delete")}
              </Button>
              <Group gap="md">
                <Button variant="default" onClick={onClose}>
                  {t("Cancel")}
                </Button>
                <Button
                  onClick={() => onUpdate(editInsuranceFormData)}
                  loading={updateInsuranceMutation.isPending}
                >
                  {t("Save")}
                </Button>
              </Group>
            </Group>
          </Stack>

          <DeleteConfirmModal
            opened={showDeleteConfirm}
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={() => {
              if (insuranceId) {
                onDelete(insuranceId)
                setShowDeleteConfirm(false)
              }
            }}
            deleting={deleteInsuranceMutation.isPending}
          />
        </>
      )}
    </DefaultModal>
  )
}
